import React, { useState, useEffect, useCallback } from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { Typography, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  GridColumns,
  GridRowParams,
  MuiEvent,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridEnrichedColDef,
  GridSortModel,
  ValueOptions,
} from "@mui/x-data-grid-pro";
import { IColumn, ISetupDetails, IDropdownItems, IButton } from "./interfaces";
import { toLowerCaseObject } from "../../utils/to-lowercase-object";
import {
  BASE_URL,
  OCP_APIM_SUBSCRIPTION_KEY,
  getAxios,
  route,
} from "../../utils/endpoints";

type ProcessMode = "edit" | "create" | null;

interface PrimaryKeys {
  [key: string]: any;
}

interface NewRecord {
  [key: string]: any;
  id: string;
  isNew?: boolean;
}

const getDatagridType = (type: string): string => {
  const typeMap: Record<string, string> = {
    varchar: "string",
    int: "number",
    datetime: "dateTime",
  };
  return typeMap[type] || "string";
};

const createEmptyRecord = (columns: IColumn[]): NewRecord => {
  const sortedColumns = [...columns].sort((a, b) => a.order - b.order);
  const record: NewRecord = { id: "" };

  sortedColumns.forEach((col) => {
    record[col.name.toLowerCase()] = "";
  });

  return record;
};

const extractPrimaryKeyString = (columns: IColumn[]): string => {
  const keys = columns
    .filter((col) => col.isPrimaryKey)
    .map((col) => col.name)
    .join(", ");

  return keys;
};

const extractPrimaryKeyValues = (
  columns: IColumn[],
  row: GridRowModel,
): PrimaryKeys => {
  const sortedColumns = [...columns].sort((a, b) => a.order - b.order);
  const pKeys: PrimaryKeys = {};
  let counter = 0;

  sortedColumns.forEach((column) => {
    if (column.isPrimaryKey) {
      counter++;
      pKeys[`_pkfield${counter}`] = row[column.name.toLowerCase()];
    }
  });

  return pKeys;
};

const fetchSetupDetails = async (tableName: string): Promise<ISetupDetails> => {
  const response = await fetch(
    `${BASE_URL}${route.apiTable}/${tableName}/setup-details?filter=&search=`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch setup details: ${response.statusText}`);
  }

  return response.json();
};

const fetchTableRows = async (
  tableName: string,
  params: {
    filter: string;
    search: string;
    orderBy: GridSortModel | undefined;
    startIndex: number;
    pageSize: number;
  },
): Promise<any[]> => {
  const orderByString =
    params.orderBy && params.orderBy.length > 0
      ? `${params.orderBy[0].field} ${params.orderBy[0].sort}`
      : "";

  const response = await getAxios(
    `${route.apiTable}/${tableName}/values`,
    "GET",
    undefined,
    {
      ...params,
      orderBy: orderByString,
    },
  );

  return response.data;
};

const updateTableRow = async (
  tableName: string,
  values: Record<string, any>,
  primaryKeys: PrimaryKeys,
): Promise<void> => {
  const bodyJSON = JSON.stringify({
    TrailerPrimaryKey: null,
    Filter: null,
    Values: { ...values, ...primaryKeys },
  });

  const response = await fetch(
    `${BASE_URL}${route.apiTable}/${tableName}/values`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY,
        "Content-Type": "application/json",
      },
      body: bodyJSON,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to update row: ${response.statusText}`);
  }
};

const createTableRow = async (
  tableName: string,
  values: Record<string, any>,
  filter: string,
  primaryKeyStr: string,
): Promise<void> => {
  const bodyJSON = JSON.stringify({
    Filter: filter,
    Values: values,
    primaryKeys: primaryKeyStr,
  });

  const response = await fetch(
    `${BASE_URL}${route.apiTable}/${tableName}/values`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY,
        "Content-Type": "application/json",
      },
      body: bodyJSON,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to create row: ${response.statusText}`);
  }
};

export const useDatagridProPagination = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [orderBy, setOrderBy] = useState<GridSortModel | undefined>(undefined);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
  }, []);

  const onChangeOrderBy = useCallback((newSortModel: GridSortModel) => {
    setOrderBy(newSortModel);
  }, []);

  return {
    page,
    pageSize,
    orderBy,
    onPageChange,
    onPageSizeChange,
    onChangeOrderBy,
  };
};

export const useCustomDatagridPro = (
  tableName: string,
  page: number,
  pageSize: number,
  orderBy: GridSortModel | undefined,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [sourceColumns, setSourceColumns] = useState<IColumn[]>([]);
  const [gridColumns, setGridColumns] = useState<GridColumns>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecordCount, setTotalRecordCount] = useState(0);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [isFilterEnabled, setIsFilterEnabled] = useState(false);
  const [buttons, setButtons] = useState<IButton[]>([]);
  const [dropdownItems, setDropdownItems] = useState<IDropdownItems[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [processMode, setProcessMode] = useState<ProcessMode>(null);
  const [primaryKeyStr, setPrimaryKeyStr] = useState("");

  const convertColumnsToGridColumns = useCallback(
    (columns: IColumn[]) => {
      const cols: GridColumns = columns.map((column) => {
        const valueOptions: ValueOptions[] | undefined =
          column.dropdownItems?.map((item) => ({
            label: item.description,
            value: item.id,
          }));

        return {
          field: column.name.toLowerCase(),
          headerName: column.heading,
          width: 300,
          type: column.isDropdown
            ? "singleSelect"
            : getDatagridType(column.type),
          valueOptions: valueOptions || [],
          headerAlign: "center" as const,
          align: "center" as const,
          resizable: false,
          editable: !column.readOnly,
          renderHeader: (params) => (
            <Typography fontWeight="bold">
              {params.colDef.headerName}
            </Typography>
          ),
          renderCell: (params) => {
            const value = params.value ? String(params.value) : "n/a";
            return <Typography>{value}</Typography>;
          },
        };
      });

      const actionsColumn: GridEnrichedColDef = {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        cellClassName: "actions",
        getActions: (params) => {
          const isInEditMode =
            rowModesModel[params.id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                sx={{ color: "primary.main" }}
                onClick={() => handleSaveClick(params.id)}
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={() => handleCancelClick(params.id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={() => handleEditClick(params.id, params.row)}
              color="inherit"
            />,
          ];
        },
      };

      setGridColumns([...cols, actionsColumn]);
    },
    [rowModesModel],
  );

  const handleEditClick = useCallback((id: GridRowId, row: any) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
    setProcessMode("edit");
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));

      const editedRow = rows.find((row) => row.id === id);
      if (editedRow?.isNew) {
        setRows((prev) => prev.filter((row) => row.id !== id));
      }
    },
    [rows],
  );

  const handleAddClick = useCallback(() => {
    const newRecord = createEmptyRecord(sourceColumns);
    const primaryKeys = extractPrimaryKeyString(sourceColumns);

    newRecord.isNew = true;

    setRows((prev) => [...prev, newRecord]);
    setProcessMode("create");
    setPrimaryKeyStr(primaryKeys);
    setRowModesModel((prev) => ({
      ...prev,
      [newRecord.id]: { mode: GridRowModes.Edit },
    }));
  }, [sourceColumns]);

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (
    params,
    event,
  ) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event,
  ) => {
    event.defaultMuiPrevented = true;
  };

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel) => {
      const updatedRow = { ...newRow, isNew: false };

      try {
        if (processMode === "edit") {
          const primaryKeys = extractPrimaryKeyValues(
            sourceColumns,
            updatedRow,
          );
          await updateTableRow(tableName, updatedRow, primaryKeys);
        } else if (processMode === "create") {
          await createTableRow(tableName, newRow, filter, primaryKeyStr);
        }
        return updatedRow;
      } catch (error) {
        console.error("Error processing row update:", error);
        throw error;
      }
    },
    [processMode, sourceColumns, tableName, filter, primaryKeyStr],
  );

  const onRowModesModelChange = useCallback((newModel: GridRowModesModel) => {
    setRowModesModel(newModel);
  }, []);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const handleFilterChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      setFilter(event.target.value as string);
    },
    [],
  );

  const loadSetupDetails = useCallback(async () => {
    try {
      const setupDetails = await fetchSetupDetails(tableName);

      setIsFilterEnabled(setupDetails.dropdownEnabled);
      setIsSearchEnabled(setupDetails.searchEnabled);

      if (setupDetails.dropdownItems) {
        setDropdownItems(setupDetails.dropdownItems);
        setFilter(setupDetails.dropdownItems[0].id as string);
      }

      if (setupDetails.buttons) {
        setButtons(setupDetails.buttons);
      }

      if (setupDetails.columns) {
        const visibleColumns = setupDetails.columns
          .filter((col) => col.visible)
          .sort((a, b) => a.order - b.order);

        setSourceColumns(visibleColumns);
        convertColumnsToGridColumns(visibleColumns);
      }
    } catch (error) {
      console.error("Error loading setup details:", error);
    }
  }, [tableName, convertColumnsToGridColumns]);

  const loadRows = useCallback(async () => {
    setIsLoading(true);

    try {
      const data = await fetchTableRows(tableName, {
        filter,
        search,
        orderBy,
        startIndex: pageSize * page,
        pageSize,
      });

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format: expected array");
      }

      const lowerCaseRows = data.map((row) => toLowerCaseObject(row));
      setRows(lowerCaseRows);

      if (data.length > 0 && data[0]._overall_count_) {
        setTotalRecordCount(data[0]._overall_count_);
      }
    } catch (error) {
      console.error("Error loading rows:", error);
    } finally {
      setIsLoading(false);
    }
  }, [tableName, page, pageSize, orderBy, filter, search]);

  useEffect(() => {
    loadSetupDetails();
  }, [loadSetupDetails]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return {
    isLoading,
    isFilterEnabled,
    isSearchEnabled,
    columns: gridColumns,
    rows,
    totalRecordCount,
    rowModesModel,
    buttons,
    dropdownItems,
    filter,
    search,
    getRows: loadRows,
    onHandleAddClick: handleAddClick,
    onChange: handleSearchChange,
    onChangeSelect: handleFilterChange,
    handleRowEditStart,
    handleRowEditStop,
    onRowModesModelChange,
    processRowUpdate,
  };
};
